import React, { useEffect, useState, useCallback, useMemo } from 'react';
import moment from 'moment-timezone';
import {
  AccessRightDTO,
  CurriculumProgressDTO,
} from '@peartherapeutics/hylia-api-client';
import Header from './Header';
import { drawLessonUsedChart, fillDays } from './utils';
import DateRangeSelector from '../../../../common/DateRangeSelector';
import { shiftDate } from '../../../../../utils/dateTime';
import styles from './styles.module.scss';

export interface ILessonChartProps {
  timezone: string;
  minDate: string;
  maxDate: string;
  accessRight: AccessRightDTO;
  curriculumProgress: CurriculumProgressDTO;
}

const LessonChart = (props: ILessonChartProps) => {
  const { minDate, maxDate, timezone, curriculumProgress, accessRight } = props;
  const startDateDefault = shiftDate(new Date(minDate), timezone).toISOString();
  const shiftedEndDate = shiftDate(new Date(maxDate), timezone).toISOString();
  const shiftedNow = shiftDate(new Date(), timezone).toISOString();
  const endDateDefault =
    moment(maxDate).tz(timezone).diff(moment().tz(timezone), 'day') > 0
      ? shiftedNow
      : shiftedEndDate;
  const [startDate, setStartDate] = useState<string>(startDateDefault);
  const [endDate, setEndDate] = useState<string>(endDateDefault);
  const [coreLessonsHeader, setCoreLessonsHeader] = useState<string>('');
  const [supplementalLessonsHeader, setSupplementalLessonsHeader] =
    useState<string>('');
  const elementId = 'lesson-used-chart';

  useEffect(() => {
    const [group1Header, group2Header] = curriculumProgress.lessonGroups.map(
      (lg) => lg.name,
    );
    setCoreLessonsHeader(group1Header);
    setSupplementalLessonsHeader(group2Header);
  }, [curriculumProgress]);

  const allDays = useMemo(() => {
    return fillDays(moment(minDate).tz(timezone), moment(maxDate).tz(timezone));
  }, [minDate, maxDate, timezone]);

  const draw = useCallback(() => {
    let lastLessonUsage = moment(curriculumProgress.lastLessonUsageDate);
    const useEndDate =
      curriculumProgress.lastLessonUsageDate &&
      moment(endDate).isAfter(lastLessonUsage)
        ? lastLessonUsage
        : moment(endDate);
    drawLessonUsedChart({
      elementId,
      timezone,
      lessonGroups: curriculumProgress.lessonGroups,
      allDays,
      startDate: moment(startDate),
      endDate: useEndDate,
    });
  }, [timezone, curriculumProgress, startDate, endDate, allDays]);

  const handleChangeDate = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  useEffect(() => {
    draw();
  }, [draw]);

  return (
    <div className={styles.container} data-testid="lessons-chart">
      <Header
        supplementalLessonsHeader={supplementalLessonsHeader}
        coreLessonsHeader={coreLessonsHeader}
      />
      <div className={styles['date-range']}>
        <DateRangeSelector
          onChange={handleChangeDate}
          startDate={startDate}
          endDate={endDate}
          minDate={startDateDefault}
          maxDate={endDateDefault}
          defaultRange={accessRight.daysRemaining === 0 ? 'all' : '1m'}
        />
      </div>
      <div id={elementId} />
    </div>
  );
};

export default LessonChart;
